// ============================================================
// Chess Arena — Input Handler (Drag-and-Drop)
// ============================================================
// Extracted from BattleScene. Handles pointer down/move/up,
// drag threshold, valid-move highlights, tutorial gating,
// targeting mode intercept, and premove queuing.
// ============================================================

import Phaser from 'phaser';
import { GridPos, PieceState } from './types';
import { Board } from './board';
import { Piece } from './piece';
import { getValidMoves } from './moveValidator';
import eventBus from './eventBus';

// -----------------------------------------------------------------
// Callback interface
// -----------------------------------------------------------------

export interface InputHandlerCallbacks {
  getAllPieceStates: () => PieceState[];
  getPieceById: (id: string) => Piece | undefined;
  getCellSize: () => number;
  getCellH: () => number;
  getBoardOffset: () => { x: number; y: number };
  getDragThreshold: () => number;

  // State queries
  isBattlePlaying: () => boolean;
  isTargetingMode: () => boolean;
  handleTargetingClick: (worldX: number, worldY: number) => void;

  // Tutorial gating
  getTutorialAllowedTarget: () => string | null;
  getLastMovedPieceId: () => string | null;
  isTutorialActive: () => boolean;
  onTutorialDragStart: () => void;
  onTutorialDragEnd: () => void;
  clearTutorialHighlights: () => void;
  filterMoves: (piece: Piece, moves: GridPos[], allStates: PieceState[]) => GridPos[];

  // Premove
  isPremoveUnlocked: () => boolean;

  // Move execution
  executeMove: (pieceId: string, target: GridPos) => void;
}

// -----------------------------------------------------------------
// InputHandler
// -----------------------------------------------------------------

export class InputHandler {
  private scene: Phaser.Scene;
  private board: Board;
  private callbacks: InputHandlerCallbacks;

  // Drag state
  private dragPiece: Piece | null = null;
  private dragStartPos: { x: number; y: number } | null = null;
  private dragLifted = false;
  private dragGhost: Phaser.GameObjects.Image | null = null;
  private validMoveHighlights: Phaser.GameObjects.Graphics | null = null;

  constructor(scene: Phaser.Scene, board: Board, callbacks: InputHandlerCallbacks) {
    this.scene = scene;
    this.board = board;
    this.callbacks = callbacks;
    this.setupInput();
  }

  private setupInput(): void {
    this.scene.input.on('pointerdown', this.onPointerDown, this);
    this.scene.input.on('pointermove', this.onPointerMove, this);
    this.scene.input.on('pointerup', this.onPointerUp, this);
  }

  /** Clean up listeners (call on scene shutdown). */
  destroy(): void {
    this.scene.input.off('pointerdown', this.onPointerDown, this);
    this.scene.input.off('pointermove', this.onPointerMove, this);
    this.scene.input.off('pointerup', this.onPointerUp, this);
    this.clearValidMoveHighlights();
    if (this.dragGhost) {
      this.dragGhost.destroy();
      this.dragGhost = null;
    }
  }

  // ------------------------------------------------------------------
  // Pointer handlers
  // ------------------------------------------------------------------

  private onPointerDown(pointer: Phaser.Input.Pointer): void {
    // Only process input when battle is playing
    if (!this.callbacks.isBattlePlaying()) return;

    // Targeting mode intercept
    if (this.callbacks.isTargetingMode()) {
      this.callbacks.handleTargetingClick(pointer.x, pointer.y);
      return;
    }

    const cellSize = this.callbacks.getCellSize();
    const cellH = this.callbacks.getCellH();
    const offset = this.callbacks.getBoardOffset();
    const gridPos = Board.pixelToGrid(pointer.x, pointer.y, cellSize, offset.x, offset.y, cellH);
    if (!gridPos) return;

    const cell = this.board.getCellContent(gridPos);
    if (!cell || !cell.pieceId) return;

    const piece = this.callbacks.getPieceById(cell.pieceId);
    if (!piece || piece.pieceState.team !== 'player') return;
    if (!piece.pieceState.isAlive) return;

    // Tutorial gating
    if (this.callbacks.isTutorialActive()) {
      const allowed = this.callbacks.getTutorialAllowedTarget();
      // Block all interaction during info-only or explicitly blocked steps
      if (allowed === '__blocked__') return;
      if (allowed) {
        if (allowed === 'cooldownPiece') {
          if (piece.pieceState.cooldownRemaining <= 0) return;
        } else if (allowed === 'lastMovedPiece') {
          if (piece.pieceState.id !== this.callbacks.getLastMovedPieceId()) return;
        } else {
          const teamPrefix = piece.pieceState.team === 'player' ? 'player' : 'enemy';
          const pieceKey = `${teamPrefix}${piece.pieceState.type}`.toLowerCase();
          const allowedTargets = allowed.split(',').map((t) => t.trim().toLowerCase());
          if (!allowedTargets.includes(pieceKey)) return;
        }
      }
    }

    // Start drag
    this.dragPiece = piece;
    this.dragStartPos = { x: pointer.x, y: pointer.y };

    // Show valid move highlights immediately on pointerDown
    // (also for cooldown pieces when premove is unlocked)
    if (!piece.pieceState.isMoving) {
      const premoveUnlocked = this.callbacks.isPremoveUnlocked();
      if (piece.pieceState.cooldownRemaining <= 0 || premoveUnlocked) {
        // Clear tutorial highlights when player starts dragging (drag highlights take over)
        this.callbacks.clearTutorialHighlights();
        this.showValidMoveHighlights(piece);
      }
    }

    // During tutorial, hide overlay so the board lights up
    if (this.callbacks.isTutorialActive()) {
      this.callbacks.onTutorialDragStart();
    }
  }

  private onPointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.dragPiece || !this.dragStartPos) return;

    const dx = pointer.x - this.dragStartPos.x;
    const dy = pointer.y - this.dragStartPos.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const threshold = this.callbacks.getDragThreshold();

    if (dist < threshold) return;

    // Create a ghost sprite on first drag movement; the real piece stays in place
    if (!this.dragLifted) {
      this.dragLifted = true;

      // Create a ghost image that follows the pointer
      const spriteKey = this.dragPiece.sprite.texture.key;
      this.dragGhost = this.scene.add.image(pointer.x, pointer.y, spriteKey);
      this.dragGhost.setDisplaySize(
        this.dragPiece.sprite.displayWidth,
        this.dragPiece.sprite.displayHeight,
      );
      this.dragGhost.setAlpha(0.65);
      this.dragGhost.setDepth(200);
    }

    // Move the ghost with the pointer (real piece stays put)
    if (this.dragGhost) {
      this.dragGhost.setPosition(pointer.x, pointer.y);
    }
  }

  private onPointerUp(pointer: Phaser.Input.Pointer): void {
    if (!this.dragPiece) return;

    const piece = this.dragPiece;
    this.dragPiece = null;
    this.dragStartPos = null;

    // Destroy the drag ghost; real piece never moved
    if (this.dragLifted) {
      if (this.dragGhost) {
        this.dragGhost.destroy();
        this.dragGhost = null;
      }
      this.dragLifted = false;
    }

    // Remove highlights
    this.clearValidMoveHighlights();

    // Re-show tutorial overlay (will be hidden again if step advances)
    if (this.callbacks.isTutorialActive()) {
      this.callbacks.onTutorialDragEnd();
    }

    // Determine target grid position
    const cellSize = this.callbacks.getCellSize();
    const cellH = this.callbacks.getCellH();
    const offset = this.callbacks.getBoardOffset();
    const gridPos = Board.pixelToGrid(pointer.x, pointer.y, cellSize, offset.x, offset.y, cellH);
    if (!gridPos) return;

    // If piece is on cooldown or moving, attempt premove (if unlocked)
    if (piece.pieceState.isMoving || piece.pieceState.cooldownRemaining > 0) {
      const premoveUnlocked = this.callbacks.isPremoveUnlocked();
      if (premoveUnlocked) {
        const allStates = this.callbacks.getAllPieceStates();
        const validMoves = getValidMoves(piece.pieceState, this.board, allStates);
        const isValid = validMoves.some((m) => m.col === gridPos.col && m.row === gridPos.row);
        if (isValid) {
          console.log('[DEBUG] Queuing premove for', piece.pieceState.id, 'to', JSON.stringify(gridPos));
          piece.setPremove(gridPos);
          eventBus.emit('premove_queued', { pieceId: piece.pieceState.id, target: gridPos });
        }
      }
      return;
    }

    // Tutorial: re-check piece is allowed (defense in depth against race conditions)
    if (this.callbacks.isTutorialActive()) {
      const allowed = this.callbacks.getTutorialAllowedTarget();
      if (allowed === '__blocked__') return;
      if (allowed) {
        const teamPrefix = piece.pieceState.team === 'player' ? 'player' : 'enemy';
        const pieceKey = `${teamPrefix}${piece.pieceState.type}`.toLowerCase();
        const allowedTargets = allowed.split(',').map((t) => t.trim().toLowerCase());
        if (!allowedTargets.includes(pieceKey)) return;
      }
    }

    // Validate move
    const allStates = this.callbacks.getAllPieceStates();
    let validMoves = getValidMoves(piece.pieceState, this.board, allStates);

    // Tutorial: filter moves so the piece can reach a capture target on the next step
    validMoves = this.callbacks.filterMoves(piece, validMoves, allStates);

    const isValid = validMoves.some((m) => m.col === gridPos.col && m.row === gridPos.row);

    if (!isValid) {
      return;
    }

    this.callbacks.executeMove(piece.pieceState.id, gridPos);
  }

  // ------------------------------------------------------------------
  // Move highlights (polished visuals)
  // ------------------------------------------------------------------

  private showValidMoveHighlights(piece: Piece): void {
    this.clearValidMoveHighlights();

    const allStates = this.callbacks.getAllPieceStates();
    let moves = getValidMoves(piece.pieceState, this.board, allStates);

    // Tutorial: filter moves so only reachable-then-capturable squares are shown
    moves = this.callbacks.filterMoves(piece, moves, allStates);

    const cellSize = this.callbacks.getCellSize();
    const cellH = this.callbacks.getCellH();
    const offset = this.callbacks.getBoardOffset();

    this.validMoveHighlights = this.scene.add.graphics();
    // Depth 201 so highlights show above tutorial overlay (200)
    this.validMoveHighlights.setDepth(201);

    for (const move of moves) {
      const pixel = Board.gridToPixel(move, cellSize, offset.x, offset.y, cellH);
      // Tinted cell background (green overlay on the cell)
      this.validMoveHighlights.fillStyle(0x4ade80, 0.25);
      this.validMoveHighlights.fillRect(
        pixel.x - cellSize / 2, pixel.y - cellH / 2,
        cellSize, cellH,
      );
      // Glowing green circle in the center
      this.validMoveHighlights.fillStyle(0x4ade80, 0.9);
      this.validMoveHighlights.fillCircle(pixel.x, pixel.y, cellSize * 0.12);
      // Outer glow ring
      this.validMoveHighlights.fillStyle(0x4ade80, 0.3);
      this.validMoveHighlights.fillCircle(pixel.x, pixel.y, cellSize * 0.22);
    }
  }

  private clearValidMoveHighlights(): void {
    if (this.validMoveHighlights) {
      this.validMoveHighlights.destroy();
      this.validMoveHighlights = null;
    }
  }
}
